import * as React from 'react';

import {
  enumPrinterType,
  enumSidecarSortOption,
  type PrinterType,
} from '@rematter/frontend-lib-api-client';
import { Button, Col, ConsoleDialog, Select } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideCheck } from 'lucide-react';

import { printerCreateAtom } from '../../../_graphql/mutationPrinterCreate';
import { sidecarsListQueryAtom } from '../../../_graphql/querySidecarsList';
import { useTranslations } from '../../../i18n/useTranslations';

interface DemoPrinterCreateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const $sidecarsForPicker = sidecarsListQueryAtom(() => ({
  variables: { sorting: [enumSidecarSortOption.NameAsc], limit: 100 },
}));

export const DemoPrinterCreateDialog = ({
  isOpen,
  onClose,
  onSuccess,
}: DemoPrinterCreateDialogProps) => {
  const t = useTranslations();
  const toast = useToast();

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [name, setName] = React.useState('');
  const [sidecarId, setSidecarId] = React.useState<string | null>(null);
  const [printerType, setPrinterType] = React.useState<PrinterType>(
    enumPrinterType.Standard,
  );

  const createPrinter = useSetAtom(printerCreateAtom({}));
  const { data: sidecarsData } = useAtomValue($sidecarsForPicker);
  const sidecars = sidecarsData?.sidecars ?? [];

  const sidecarOptions = React.useMemo(
    () => sidecars.map((s) => ({ key: s.id, label: s.name })),
    [sidecars],
  );

  const printerTypeOptions = React.useMemo(
    () => [
      {
        key: enumPrinterType.Standard,
        label: t.hardwareView.demoCreateDialog.printerTypeOptions.Standard,
      },
      {
        key: enumPrinterType.Thermal,
        label: t.hardwareView.demoCreateDialog.printerTypeOptions.Thermal,
      },
    ],
    [t],
  );

  const canSubmit = name.trim() !== '' && sidecarId !== null;

  const resetForm = React.useCallback(() => {
    setName('');
    setSidecarId(null);
    setPrinterType(enumPrinterType.Standard);
  }, []);

  React.useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  const handleClose = React.useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleSubmit = React.useCallback(async () => {
    if (!canSubmit || !sidecarId) return;

    try {
      setIsSubmitting(true);
      await createPrinter({
        sidecarId,
        uniqueId: `demo:printer-${Date.now()}`,
        driverType: 'demo',
        name: name.trim(),
        printerType,
      });
      toast.success(t.hardwareView.demoCreateDialog.messages.success);
      onSuccess?.();
      handleClose();
    } catch (err) {
      toast.error(
        t.hardwareView.demoCreateDialog.messages.error(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    canSubmit,
    sidecarId,
    createPrinter,
    name,
    printerType,
    toast,
    onSuccess,
    handleClose,
    t,
  ]);

  const inputClass =
    'rounded border border-neutral/20 px-3 py-1.5 text-sm outline-none focus:border-primary';

  return (
    <ConsoleDialog
      className="@DemoPrinterCreateDialog"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      title={t.hardwareView.demoCreateDialog.titles.printer}
      content={
        <Col gap3>
          <Col gap1>
            <label className="text-sm text-neutral/60">
              {t.hardwareView.demoCreateDialog.fields.name}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputClass}
              disabled={isSubmitting}
            />
          </Col>
          <Col gap1>
            <label className="text-sm text-neutral/60">
              {t.hardwareView.demoCreateDialog.fields.printerType}
            </label>
            <Select
              options={printerTypeOptions}
              value={printerType}
              onChange={(v) => v && setPrinterType(v)}
            />
          </Col>
          <Col gap1>
            <label className="text-sm text-neutral/60">
              {t.hardwareView.demoCreateDialog.fields.sidecar}
            </label>
            <Select
              options={sidecarOptions}
              value={sidecarId}
              onChange={setSidecarId}
              placeholder={
                t.hardwareView.demoCreateDialog.fields.sidecarPlaceholder
              }
            />
          </Col>
        </Col>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            color="neutral"
            onPress={handleClose}
            isDisabled={isSubmitting}
          >
            {t.hardwareView.demoCreateDialog.actions.cancel}
          </Button>
          <Button
            icon={<LucideCheck />}
            color="primary"
            onPress={handleSubmit}
            isDisabled={!canSubmit || isSubmitting}
          >
            {isSubmitting
              ? t.hardwareView.demoCreateDialog.actions.creating
              : t.hardwareView.demoCreateDialog.actions.create}
          </Button>
        </div>
      }
    />
  );
};
